// components/common/Toast.js - Lightweight toast notification system
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ICONS = {
  success: <CheckCircle className="w-5 h-5 text-status-good" />,
  error:   <XCircle className="w-5 h-5 text-status-bad" />,
  warning: <AlertTriangle className="w-5 h-5 text-status-warn" />,
  info:    <Info className="w-5 h-5 text-accent-ink" />,
};

const COLORS = {
  success: 'border-[var(--ok-soft)] bg-[var(--ok-soft)]',
  error:   'border-[var(--error-soft)] bg-[var(--error-soft)]',
  warning: 'border-accent-subtle bg-accent-soft',
  info:    'border-accent-subtle bg-accent-soft',
};

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

function Toast({ toast, onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg ${COLORS[toast.type]} animate-slide-in`}>
      {ICONS[toast.type]}
      <p className="text-sm text-read flex-1">{toast.message}</p>
      <button onClick={() => onDismiss(toast.id)} className="text-faint hover:text-mutedink">
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'error', 6000),
    warning: (msg) => addToast(msg, 'warning'),
    info: (msg) => addToast(msg, 'info'),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-[100] space-y-2 max-w-sm">
        {toasts.map(t => (
          <Toast key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
